import type { Difficulty, EventSummary, EventType } from "@/lib/types/eventList";
import { asLocalDate, dayOf, daysBetween, eventHasEnded } from "@/lib/eventTime";
import { euros } from "@/lib/format";
import { eventItem, track } from "@/lib/analytics";

/**
 * What a card says about an event, worked out from the list's fields: the
 * current app's models/eventTemplates.js and models/registrationDrafts.js, in
 * plain functions the React pieces draw from.
 */

// the six grades of difficulty, easiest first: three levels of two grades each
export const DIFFICULTIES: Difficulty[] = ["EASY_1", "EASY_2", "MODERATE_1", "MODERATE_2", "HARD_1", "HARD_2"];

export const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  EASY_1: "Fácil I",
  EASY_2: "Fácil II",
  MODERATE_1: "Moderado I",
  MODERATE_2: "Moderado II",
  HARD_1: "Difícil I",
  HARD_2: "Difícil II",
};

export const TYPE_LABELS: Record<EventType, string> = {
  HIKE: "Caminhada",
  CIRCLE: "Círculo Aberto",
  CAMPING: "Acampamento",
  PARTY: "Festa",
  SOIREE: "Serão",
  SALON: "Tertúlia",
};

export const typeLabel = (type: string | null | undefined) => (type && TYPE_LABELS[type as EventType]) || type || "";

export const difficultyLabel = (difficulty: string | null | undefined) =>
  (difficulty && DIFFICULTY_LABELS[difficulty as Difficulty]) || "";

/** the level of a grade in one word -- 'Fácil', 'Moderado', 'Difícil' -- for where there is no room for the grade */
export const difficultyLevelLabel = (difficulty: string | null | undefined) => difficultyLabel(difficulty).replace(/ I+$/, "");

/** 1..6 for one of the six grades, 0 for anything else */
export const difficultyGrade = (difficulty: string | null | undefined) => DIFFICULTIES.indexOf(difficulty as Difficulty) + 1;

/** the difficulty an event is shown with: none for a Círculo Aberto, which is not walked */
export const shownDifficulty = (event: { type?: string | null; difficulty?: string | null }) => (event.type === "CIRCLE" ? null : event.difficulty);

/**
 * An event's format, by the nights it takes: none is a day, one or two a
 * weekend, three or more a trip. The same split as Explore's Duração filter
 * (public-api's eventService.js), so a card never says other than the filter
 * that found it.
 */
export type EventFormat = "SINGLE_DAY" | "WEEKEND" | "TRIP";

export const eventNights = (event: { start_time?: string | null; end_time?: string | null }) =>
  event.start_time && event.end_time ? Math.max(0, daysBetween(dayOf(event.start_time), dayOf(event.end_time))) : 0;

export const eventFormat = (event: { start_time?: string | null; end_time?: string | null }): EventFormat => {
  const nights = eventNights(event);
  return nights < 1 ? "SINGLE_DAY" : nights <= 2 ? "WEEKEND" : "TRIP";
};

/**
 * What a card says an event is, over its title: the format for anything walked
 * -- with how long, for a trip or a weekend -- and the type itself for what is
 * not a walk (a Círculo Aberto, a Festa), where a day or a weekend says little.
 */
export const cardKind = (event: EventSummary): { kind: EventFormat | "SOCIAL"; text: string; detail: string } => {
  const walked = !event.type || event.type === "HIKE" || event.type === "CAMPING";
  if (!walked) return { kind: "SOCIAL", text: typeLabel(event.type), detail: "" };
  const nights = eventNights(event);
  const format = eventFormat(event);
  if (format === "SINGLE_DAY") return { kind: format, text: "1 dia", detail: "" };
  const detail = `${nights + 1} dias · ${nights} ${nights === 1 ? "noite" : "noites"}`;
  return { kind: format, text: format === "WEEKEND" ? "Fim de semana" : "Viagem", detail };
};

/**
 * Whether an event is behind us, at the later of its start and its end: a past
 * event's card leads with its photos and shows no prices.
 */
export const isPastEvent = (event: EventSummary, now?: string) => eventHasEnded(event, now);

/** the banner the card shows: the event's first, else its programme's (already in that order) */
export const eventBanner = (event: EventSummary) => (event.banners || [])[0] || null;

/**
 * Whether the client is going: registered, and not still to pay. The event's
 * own page is what a card opens either way; this decides what the card says
 * in place of the prices, and what the page's way to register becomes
 * (the current app's models/inscricao.js opensAsMyEvent).
 */
export const opensAsMyEvent = (event: EventSummary | null | undefined) => !!event && !!event.is_registered && !event.awaiting_payment;

/**
 * Whether the event has no places left for this client: full, and they are not
 * already going (registrationDrafts.js isSoldOutFor).
 */
export const isSoldOutFor = (event: EventSummary | null | undefined) =>
  !!event && event.availability === "SOLD_OUT" && !event.is_registered;

/**
 * Whether a card says Lista de espera aberta: a full event still taking
 * registrations, to anyone not already on its list (who reads Na lista de
 * espera instead). Explore's card and Início's strip both ask it.
 */
export const waitingListOpenFor = (event: EventSummary | null | undefined) =>
  !!event && event.registration_state === "OPEN" && isSoldOutFor(event) && !(Number(event.on_waiting_list) > 0);

/**
 * Whether the event's page has something where the way to register goes: the
 * way to register while registration is open, and for a client already going
 * the way to their inscrição, whatever the registration's state
 * (registrationDrafts.js showsRegisterButton).
 */
export const showsRegisterButton = (event: EventSummary | null | undefined) =>
  !!event && (event.registration_state === "OPEN" || opensAsMyEvent(event));

/**
 * The label before a card's title, for an event still taking registrations:
 * Últimas vagas in the last 30% of the places -- Último lugar with only the one
 * -- and Esgotado with none; else Novidade in the ten days after its registrations
 * opened, which gives way to the other two. Nothing for a client already going,
 * and Na lista de espera for one waiting for places on it.
 */
export const cardBadge = (event: EventSummary): { text: string; tone: "amber" | "danger" | "brand" } | null => {
  if (event.is_registered) return null;
  if (Number(event.on_waiting_list) > 0) return { text: "Na lista de espera", tone: "brand" };
  switch (event.availability) {
    case "LAST_PLACES":
      return { text: event.places_left === 1 ? "Último lugar" : "Últimas vagas", tone: "amber" };
    case "SOLD_OUT":
      return { text: "Esgotado", tone: "danger" };
    default:
      return event.is_new ? { text: "Novidade", tone: "brand" } : null;
  }
};

export type PriceRow = { name: string; price: string; late: string | null };

/**
 * The prices an event charges today, a row per type that applies: Adulto always,
 * Membro when cheaper than Adulto, Criança when the event takes children. While
 * an early price lasts, the later price each goes up to is given beside it, to be
 * shown crossed out (eventTemplates.js priceRow).
 */
export const priceRows = (event: EventSummary): PriceRow[] => {
  const row = (name: string, price: number | null, late: number | null): PriceRow => ({
    name,
    price: euros(price),
    late: late !== null && Number(late) > Number(price) ? euros(late) : null,
  });
  const rows = [row("Adulto", event.price, event.late_price)];
  if (event.price_member !== null && event.price_member !== undefined && Number(event.price) > Number(event.price_member)) {
    rows.push(row("Membro", event.price_member, event.late_price_member));
  }
  if (Number(event.price_child) > 0) {
    rows.push(row("Criança", event.price_child, event.late_price_child));
  }
  return rows;
};

/** 'Preço válido até 8 out', while an early price lasts */
export const priceValidUntilText = (event: EventSummary) => {
  const d = asLocalDate(event.price_valid_until);
  if (!d) return "";
  const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `Preço válido até ${d.getDate()} ${months[d.getMonth()]}`;
};

/** 'Realizado em outubro de 2026', for a past event's card */
export const heldInText = (event: EventSummary) => {
  const d = asLocalDate(event.start_time);
  if (!d) return "";
  const months = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
  return `Realizado em ${months[d.getMonth()]} de ${d.getFullYear()}`;
};

/**
 * The name GA is given for the list an event was picked from: the tab and the
 * way it was shown, 'Explorar · Lista', 'Favoritos · Mapa' (the current app's
 * eventListName).
 */
export type ListWay = "Lista" | "Mapa" | "Calendário";

export const listNameFor = (tabTitle: string, way: ListWay) => `${tabTitle} · ${way}`;

/** an event picked from a list, counted with the list it was picked from */
export const trackSelectItem = (event: EventSummary, listName: string) =>
  track("select_item", { item_list_name: listName, items: [{ ...eventItem(event), item_list_name: listName }] });

/** a title as typed in, stray spaces and all */
export const eventTitle = (event: { title?: string | null }) => String(event.title || "").trim();
